import Box from "@/elements/Box";
import Input from "@/elements/Input";
import Typography from "@/elements/Typography";
import {
  FormHelperText,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Slide,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  RichiestaPreventivoCreateDto,
  RichiestaPreventivoTipo,
  TipoOlioLavorazioni,
  TipoOlioLubrifica,
} from "@/api";

interface Step3DettagliProps {
  activeStep: number;
  slideDirection: "left" | "right";
  tipoRichiesta: RichiestaPreventivoCreateDto["tipo"];
  tipoOlioLavorazioni?: RichiestaPreventivoCreateDto["tipoOlioLavorazioni"];
  register: UseFormRegister<RichiestaPreventivoCreateDto>;
  control: Control<RichiestaPreventivoCreateDto>;
  errors: FieldErrors<RichiestaPreventivoCreateDto>;
}

export function Step3_Dettagli({
  activeStep,
  slideDirection,
  tipoRichiesta,
  tipoOlioLavorazioni,
  register,
  control,
  errors,
}: Step3DettagliProps) {
  const { t } = useTranslation();

  return (
    <Slide
      direction={slideDirection}
      in={activeStep === 3}
      timeout={500}
      unmountOnExit
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          bgcolor: "white",
          position: activeStep === 3 ? "relative" : "absolute",
          width: "100%",
          top: 0,
        }}
      >
        <Box>
          {tipoRichiesta === RichiestaPreventivoTipo.lubrifica && (
            <>
              <Typography
                variant="h5"
                gutterBottom
                mb={3}
                sx={{ color: "#333" }}
              >
                {t("richiestaPreventivo.step3.lubrifica.title")}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box mb={1} ml={0.5}>
                    <Typography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                    >
                      {t("richiestaPreventivo.step3.lubrifica.tipoOlio")}
                    </Typography>
                  </Box>
                  <Controller
                    name="tipoOlioLubrifica"
                    control={control}
                    rules={{}}
                    render={({ field }) => (
                      <Select
                        {...field}
                        fullWidth
                        displayEmpty
                        renderValue={
                          field.value
                            ? undefined
                            : () => (
                                <span style={{ color: "#adb5bd" }}>
                                  {t(
                                    "richiestaPreventivo.step3.lubrifica.tipoOlioPlaceholder",
                                  )}
                                </span>
                              )
                        }
                        sx={{ "& .MuiSelect-select": { padding: "10px 12px" } }}
                      >
                        <MenuItem value="" disabled>
                          {t(
                            "richiestaPreventivo.step3.lubrifica.tipoOlioPlaceholder",
                          )}
                        </MenuItem>
                        <MenuItem value={TipoOlioLubrifica.idraulico}>
                          {t("richiestaPreventivo.step3.lubrifica.idraulico")}
                        </MenuItem>
                        <MenuItem value={TipoOlioLubrifica.riduttori}>
                          {t("richiestaPreventivo.step3.lubrifica.riduttori")}
                        </MenuItem>
                        <MenuItem value={TipoOlioLubrifica.turbine}>
                          {t("richiestaPreventivo.step3.lubrifica.turbine")}
                        </MenuItem>
                        <MenuItem value={TipoOlioLubrifica.lubrifica}>
                          {t("richiestaPreventivo.step3.lubrifica.lubrifica")}
                        </MenuItem>
                        <MenuItem value={TipoOlioLubrifica.acquaGlicole}>
                          {t(
                            "richiestaPreventivo.step3.lubrifica.acquaGlicole",
                          )}
                        </MenuItem>
                      </Select>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box mb={1} ml={0.5}>
                    <Typography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                    >
                      {t(
                        "richiestaPreventivo.step3.lubrifica.frequenzaCambioOlio",
                      )}{" "}
                      *
                    </Typography>
                  </Box>
                  <Controller
                    name="frequenzaAnniDiCambioOlio"
                    control={control}
                    rules={{
                      required: t("richiestaPreventivo.step3.errors.required"),
                    }}
                    render={({ field }) => (
                      <RadioGroup
                        {...field}
                        row
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <FormControlLabel
                          value={1}
                          control={<Radio />}
                          label={t("richiestaPreventivo.step3.lubrifica.anno1")}
                        />
                        <FormControlLabel
                          value={2}
                          control={<Radio />}
                          label={t("richiestaPreventivo.step3.lubrifica.anno2")}
                        />
                        <FormControlLabel
                          value={3}
                          control={<Radio />}
                          label={t("richiestaPreventivo.step3.lubrifica.anno3")}
                        />
                      </RadioGroup>
                    )}
                  />
                  {errors.frequenzaAnniDiCambioOlio?.message && (
                    <FormHelperText error>
                      {errors.frequenzaAnniDiCambioOlio.message}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box mb={1} ml={0.5}>
                    <Typography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                    >
                      {t("richiestaPreventivo.step3.lubrifica.quantitaOlio")} *
                    </Typography>
                  </Box>
                  <Input
                    fullWidth
                    type="number"
                    placeholder={t(
                      "richiestaPreventivo.step3.lubrifica.quantitaOlioPlaceholder",
                    )}
                    {...register("quantitaOlioLubrifica", {
                      min: {
                        value: 0,
                        message: t("richiestaPreventivo.step3.errors.minZero"),
                      },
                      valueAsNumber: true,
                    })}
                    error={!!errors.quantitaOlioLubrifica?.message}
                  />
                  {errors.quantitaOlioLubrifica?.message && (
                    <FormHelperText error>
                      {errors.quantitaOlioLubrifica?.message}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box mb={1} ml={0.5}>
                    <Typography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                    >
                      {t("richiestaPreventivo.step3.lubrifica.prezzoOlio")} *
                    </Typography>
                  </Box>
                  <Input
                    fullWidth
                    type="number"
                    placeholder={t(
                      "richiestaPreventivo.step3.lubrifica.prezzoOlioPlaceholder",
                    )}
                    {...register("prezzoOlioLubrifica", {
                      min: {
                        value: 0,
                        message: t("richiestaPreventivo.step3.errors.minZero"),
                      },
                      valueAsNumber: true,
                    })}
                    error={!!errors.prezzoOlioLubrifica?.message}
                  />
                  {errors.prezzoOlioLubrifica?.message && (
                    <FormHelperText error>
                      {errors.prezzoOlioLubrifica?.message}
                    </FormHelperText>
                  )}
                </Grid>

                {/* Pompe */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: "#666", textTransform: "uppercase", letterSpacing: 0.8, display: "block", mb: 1.5 }}>
                      {t("richiestaPreventivo.step3.lubrifica.gruppoPompe")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mb={1} ml={0.5}>
                          <Typography component="label" variant="caption" fontWeight="bold">
                            {t("richiestaPreventivo.step3.lubrifica.numeroPompeCambiateAnno")}
                          </Typography>
                        </Box>
                        <Input fullWidth type="number"
                          placeholder={t("richiestaPreventivo.step3.lubrifica.numeroPompeCambiateAnnoPlaceholder")}
                          {...register("numeroPompeCambiateAnno", { min: { value: 0, message: t("richiestaPreventivo.step3.errors.minZero") }, valueAsNumber: true })}
                          error={!!errors.numeroPompeCambiateAnno?.message}
                        />
                        {errors.numeroPompeCambiateAnno?.message && <FormHelperText error>{errors.numeroPompeCambiateAnno.message}</FormHelperText>}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box mb={1} ml={0.5}>
                          <Typography component="label" variant="caption" fontWeight="bold">
                            {t("richiestaPreventivo.step3.lubrifica.costoUnitarioPompa")}
                          </Typography>
                        </Box>
                        <Input fullWidth type="number"
                          placeholder={t("richiestaPreventivo.step3.lubrifica.costoUnitarioPompaPlaceholder")}
                          {...register("costoUnitarioPompa", { min: { value: 0, message: t("richiestaPreventivo.step3.errors.minZero") }, valueAsNumber: true })}
                          error={!!errors.costoUnitarioPompa?.message}
                        />
                        {errors.costoUnitarioPompa?.message && <FormHelperText error>{errors.costoUnitarioPompa.message}</FormHelperText>}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Valvole */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: "#666", textTransform: "uppercase", letterSpacing: 0.8, display: "block", mb: 1.5 }}>
                      {t("richiestaPreventivo.step3.lubrifica.gruppoValvole")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mb={1} ml={0.5}>
                          <Typography component="label" variant="caption" fontWeight="bold">
                            {t("richiestaPreventivo.step3.lubrifica.numeroValvoleCambiateAnno")}
                          </Typography>
                        </Box>
                        <Input fullWidth type="number"
                          placeholder={t("richiestaPreventivo.step3.lubrifica.numeroValvoleCambiateAnnoPlaceholder")}
                          {...register("numeroValvoleCambiateAnno", { min: { value: 0, message: t("richiestaPreventivo.step3.errors.minZero") }, valueAsNumber: true })}
                          error={!!errors.numeroValvoleCambiateAnno?.message}
                        />
                        {errors.numeroValvoleCambiateAnno?.message && <FormHelperText error>{errors.numeroValvoleCambiateAnno.message}</FormHelperText>}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box mb={1} ml={0.5}>
                          <Typography component="label" variant="caption" fontWeight="bold">
                            {t("richiestaPreventivo.step3.lubrifica.costoUnitarioValvola")}
                          </Typography>
                        </Box>
                        <Input fullWidth type="number"
                          placeholder={t("richiestaPreventivo.step3.lubrifica.costoUnitarioValvolaPlaceholder")}
                          {...register("costoUnitarioValvola", { min: { value: 0, message: t("richiestaPreventivo.step3.errors.minZero") }, valueAsNumber: true })}
                          error={!!errors.costoUnitarioValvola?.message}
                        />
                        {errors.costoUnitarioValvola?.message && <FormHelperText error>{errors.costoUnitarioValvola.message}</FormHelperText>}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Fermo macchina */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fafafa" }}>
                    <Typography variant="caption" fontWeight="bold" sx={{ color: "#666", textTransform: "uppercase", letterSpacing: 0.8, display: "block", mb: 1.5 }}>
                      {t("richiestaPreventivo.step3.lubrifica.gruppoFermoMacchina")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box mb={1} ml={0.5}>
                          <Typography component="label" variant="caption" fontWeight="bold">
                            {t("richiestaPreventivo.step3.lubrifica.oreFermoMacchinaAnno")}
                          </Typography>
                        </Box>
                        <Input fullWidth type="number"
                          placeholder={t("richiestaPreventivo.step3.lubrifica.oreFermoMacchinaAnnoPlaceholder")}
                          {...register("oreFermoMacchinaAnno", { min: { value: 0, message: t("richiestaPreventivo.step3.errors.minZero") }, valueAsNumber: true })}
                          error={!!errors.oreFermoMacchinaAnno?.message}
                        />
                        {errors.oreFermoMacchinaAnno?.message && <FormHelperText error>{errors.oreFermoMacchinaAnno.message}</FormHelperText>}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box mb={1} ml={0.5}>
                          <Typography component="label" variant="caption" fontWeight="bold">
                            {t("richiestaPreventivo.step3.lubrifica.costoOrarioFermoMacchina")}
                          </Typography>
                        </Box>
                        <Input fullWidth type="number"
                          placeholder={t("richiestaPreventivo.step3.lubrifica.costoOrarioFermoMacchinaPlaceholder")}
                          {...register("costoOrarioFermoMacchina", { min: { value: 0, message: t("richiestaPreventivo.step3.errors.minZero") }, valueAsNumber: true })}
                          error={!!errors.costoOrarioFermoMacchina?.message}
                        />
                        {errors.costoOrarioFermoMacchina?.message && <FormHelperText error>{errors.costoOrarioFermoMacchina.message}</FormHelperText>}
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          {tipoRichiesta === RichiestaPreventivoTipo.lavorazioniMeccaniche && (
            <>
              <Typography
                variant="h5"
                gutterBottom
                mb={3}
                sx={{ color: "#333" }}
              >
                {t("richiestaPreventivo.step3.lavorazioni.title")}
              </Typography>

              <Grid container spacing={3}>
                <Grid
                  item
                  xs={12}
                  md={
                    tipoOlioLavorazioni ===
                    TipoOlioLavorazioni.lubrorefrigerante
                      ? 12
                      : 6
                  }
                >
                  <Box mb={1} ml={0.5}>
                    <Typography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                    >
                      {tipoOlioLavorazioni ===
                      TipoOlioLavorazioni.lubrorefrigerante
                        ? t(
                            "richiestaPreventivo.step3.lavorazioni.frequenzaSostituzioneLubro",
                          )
                        : t(
                            "richiestaPreventivo.step3.lavorazioni.frequenzaSostituzioneOlio",
                          )}{" "}
                      *
                    </Typography>
                  </Box>
                  {tipoOlioLavorazioni ===
                  TipoOlioLavorazioni.lubrorefrigerante ? (
                    <Controller
                      name="frequenzaAnniDiCambioRefrigerante"
                      control={control}
                      rules={{
                        required: t(
                          "richiestaPreventivo.step3.errors.required",
                        ),
                      }}
                      render={({ field }) => (
                        <RadioGroup
                          {...field}
                          row
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        >
                          <FormControlLabel
                            value={1}
                            control={<Radio />}
                            label={t(
                              "richiestaPreventivo.step3.lubrifica.anno1",
                            )}
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio />}
                            label={t(
                              "richiestaPreventivo.step3.lubrifica.anno2",
                            )}
                          />
                          <FormControlLabel
                            value={3}
                            control={<Radio />}
                            label={t(
                              "richiestaPreventivo.step3.lubrifica.anno3",
                            )}
                          />
                        </RadioGroup>
                      )}
                    />
                  ) : (
                    <Input
                      fullWidth
                      type="number"
                      placeholder={t(
                        "richiestaPreventivo.step3.lavorazioni.frequenzaSostituzioneExample",
                      )}
                      {...register("frequenzaAnniDiCambioRefrigerante", {
                        min: {
                          value: 1,
                          message: t(
                            "richiestaPreventivo.step3.errors.minZero",
                          ),
                        },
                        valueAsNumber: true,
                      })}
                      error={
                        !!errors.frequenzaAnniDiCambioRefrigerante?.message
                      }
                    />
                  )}
                  {errors.frequenzaAnniDiCambioRefrigerante?.message && (
                    <FormHelperText error>
                      {errors.frequenzaAnniDiCambioRefrigerante?.message}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box mb={1} ml={0.5}>
                    <Typography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                    >
                      {tipoOlioLavorazioni ===
                      TipoOlioLavorazioni.lubrorefrigerante
                        ? t(
                            "richiestaPreventivo.step3.lavorazioni.quantitaLubro",
                          )
                        : t(
                            "richiestaPreventivo.step3.lavorazioni.quantitaOlio",
                          )}{" "}
                      *
                    </Typography>
                  </Box>
                  <Input
                    fullWidth
                    type="number"
                    placeholder={t(
                      "richiestaPreventivo.step3.lavorazioni.quantitaFluidoPlaceholder",
                    )}
                    {...register("quantitaOlioLavorazioni", {
                      min: {
                        value: 0,
                        message: t("richiestaPreventivo.step3.errors.minZero"),
                      },
                      valueAsNumber: true,
                    })}
                    error={!!errors.quantitaOlioLavorazioni?.message}
                  />
                  {errors.quantitaOlioLavorazioni?.message && (
                    <FormHelperText error>
                      {errors.quantitaOlioLavorazioni?.message}
                    </FormHelperText>
                  )}
                </Grid>

                {tipoOlioLavorazioni ===
                  TipoOlioLavorazioni.lubrorefrigerante && (
                  <Grid item xs={12} md={6}>
                    <Box mb={1} ml={0.5}>
                      <Typography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                      >
                        {t(
                          "richiestaPreventivo.step3.lavorazioni.concentrazioneRifrattometria",
                        )}{" "}
                        *
                      </Typography>
                    </Box>
                    <Input
                      fullWidth
                      type="number"
                      placeholder={t(
                        "richiestaPreventivo.step3.lavorazioni.concentrazioneRifrattometriaPlaceholder",
                      )}
                      {...register("concentrazioneRifrattometria", {
                        min: {
                          value: 0,
                          message: t(
                            "richiestaPreventivo.step3.errors.minZero",
                          ),
                        },
                        max: {
                          value: 100,
                          message: t(
                            "richiestaPreventivo.step3.errors.maxCento",
                          ),
                        },
                        valueAsNumber: true,
                      })}
                      error={!!errors.concentrazioneRifrattometria?.message}
                    />
                    {errors.concentrazioneRifrattometria?.message && (
                      <FormHelperText error>
                        {errors.concentrazioneRifrattometria?.message}
                      </FormHelperText>
                    )}
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Box mb={1} ml={0.5}>
                    <Typography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                    >
                      {tipoOlioLavorazioni ===
                      TipoOlioLavorazioni.lubrorefrigerante
                        ? t("richiestaPreventivo.step3.lavorazioni.prezzoLubro")
                        : t(
                            "richiestaPreventivo.step3.lavorazioni.prezzoOlio",
                          )}{" "}
                      *
                    </Typography>
                  </Box>
                  <Input
                    fullWidth
                    type="number"
                    placeholder={t(
                      "richiestaPreventivo.step3.lavorazioni.prezzoFluidoPlaceholder",
                    )}
                    {...register("costoLitroOlio", {
                      min: {
                        value: 0,
                        message: t("richiestaPreventivo.step3.errors.minZero"),
                      },
                      valueAsNumber: true,
                    })}
                    error={!!errors.costoLitroOlio?.message}
                  />
                  {errors.costoLitroOlio?.message && (
                    <FormHelperText error>
                      {errors.costoLitroOlio?.message}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box mb={1} ml={0.5}>
                    <Typography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                    >
                      {tipoOlioLavorazioni ===
                      TipoOlioLavorazioni.lubrorefrigerante
                        ? t(
                            "richiestaPreventivo.step3.lavorazioni.spesaMediaAnnuaUtensiliLubro",
                          )
                        : t(
                            "richiestaPreventivo.step3.lavorazioni.spesaMediaAnnuaUtensiliOlio",
                          )}{" "}
                      *
                    </Typography>
                  </Box>
                  <Input
                    fullWidth
                    type="number"
                    placeholder={t(
                      "richiestaPreventivo.step3.lavorazioni.spesaMediaAnnuaUtensiliPlaceholder",
                    )}
                    {...register("spesaMediaAnnuaUtensili", {
                      min: {
                        value: 0,
                        message: t("richiestaPreventivo.step3.errors.minZero"),
                      },
                      valueAsNumber: true,
                    })}
                    error={!!errors.spesaMediaAnnuaUtensili?.message}
                  />
                  {errors.spesaMediaAnnuaUtensili?.message && (
                    <FormHelperText error>
                      {errors.spesaMediaAnnuaUtensili?.message}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box mb={1} ml={0.5}>
                    <Typography component="label" variant="caption" fontWeight="bold">
                      {t("richiestaPreventivo.step3.lavorazioni.numeroPompeAltaPressione")}
                    </Typography>
                  </Box>
                  <Input
                    fullWidth
                    type="number"
                    placeholder={t("richiestaPreventivo.step3.lavorazioni.numeroPompeAltaPressioneAlt")}
                    {...register("numeroPompeAltaPressioneLavorazioni", {
                      min: { value: 0, message: t("richiestaPreventivo.step3.errors.minZero") },
                      valueAsNumber: true,
                    })}
                    error={!!errors.numeroPompeAltaPressioneLavorazioni?.message}
                  />
                  {errors.numeroPompeAltaPressioneLavorazioni?.message && (
                    <FormHelperText error>{errors.numeroPompeAltaPressioneLavorazioni?.message}</FormHelperText>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box mb={1} ml={0.5}>
                    <Typography component="label" variant="caption" fontWeight="bold">
                      {t("richiestaPreventivo.step3.lavorazioni.costoUnitarioPompaAltaPressione")}
                    </Typography>
                  </Box>
                  <Input
                    fullWidth
                    type="number"
                    placeholder={t("richiestaPreventivo.step3.lavorazioni.costoUnitarioPompaAltaPressioneAlt")}
                    {...register("costoUnitarioPompaAltaPressione", {
                      min: { value: 0, message: t("richiestaPreventivo.step3.errors.minZero") },
                      valueAsNumber: true,
                    })}
                    error={!!errors.costoUnitarioPompaAltaPressione?.message}
                  />
                  {errors.costoUnitarioPompaAltaPressione?.message && (
                    <FormHelperText error>{errors.costoUnitarioPompaAltaPressione?.message}</FormHelperText>
                  )}
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </Paper>
    </Slide>
  );
}
